import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isProfileComplete } from "@/api/auth/auth.utils";
import { merchantQueryOptions } from "@/api/vendure/merchant/merchant.query.options";
import { eligiblePaymentMethodsQueryOptions } from "@/api/vendure/order/order.query.options";
import { MessageCircle } from "@/components/animate-ui/icons/message-circle";
import { LoginSheet } from "@/components/business/auth/LoginSheet";
import { AuthButton } from "@/components/business/auth/UserAvatar";
import { CartItemRow } from "@/components/business/cart/CartItem";
import { OrderNoteSheet } from "@/components/business/cart/OrderNoteSheet";
import { Button } from "@/components/ui/button";
import { useActivateVenue } from "@/hooks/useActivateVenue";
import { useGuestLogin } from "@/hooks/useGuestLogin";
import { formatPrice } from "@/lib/currency";
import { noindexHead } from "@/lib/seo";
import {
  completeProfileOnConfirmAtom,
  completeProfileSheetOpenAtom,
  currentUserAtom,
  guestDetailsAtom,
  isAuthAtom,
} from "@/stores/auth.store";
import { cartCommentAtom, cartStateAtom } from "@/stores/cart.store";
import {
  currencyCodeAtom,
  deliveryMethodAtom,
  loginSheetOpenAtom,
} from "@/stores/delivery.store";

export const Route = createFileRoute("/business/$slug_/cart")({
  ssr: false,
  head: noindexHead,
  component: Sentry.withProfiler(CartPage, { name: "CartPage" }),
});

function CartPage() {
  useActivateVenue();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams({ strict: false }) as { slug: string };
  const { items, totalPrice } = useAtomValue(cartStateAtom);
  const cartComment = useAtomValue(cartCommentAtom);
  const [noteOpen, setNoteOpen] = useState(false);
  const currency = useAtomValue(currencyCodeAtom);
  const isAuth = useAtomValue(isAuthAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const guestDetails = useAtomValue(guestDetailsAtom);
  const deliveryMethod = useAtomValue(deliveryMethodAtom);
  const isDineIn = deliveryMethod === "dine_in";
  const { data: venues } = useQuery(merchantQueryOptions(slug));
  const venueToken = venues?.[0]?.token;
  const guestInfoLevel = venues?.[0]?.guest_info_level ?? null;
  const { data: paymentMethods = [], isLoading: methodsLoading } = useQuery(
    eligiblePaymentMethodsQueryOptions(venueToken ?? "", !!venueToken),
  );
  const noPaymentMethods =
    !methodsLoading &&
    !!venueToken &&
    !paymentMethods.some((method) => method.isEligible);
  const openLoginSheet = useSetAtom(loginSheetOpenAtom);
  const openCompleteProfile = useSetAtom(completeProfileSheetOpenAtom);
  const setCompleteProfileCallback = useSetAtom(completeProfileOnConfirmAtom);
  const { guestLogin: silentGuestLogin } = useGuestLogin(goToCheckout, false);

  function goToCheckout() {
    navigate({
      to: "/business/$slug/checkout",
      params: { slug },
      viewTransition: true,
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="min-h-screen flex flex-col"
    >
      <div className="sticky top-0 z-10 flex items-center gap-3 p-4 bg-gradient-to-b from-background via-background/40 to-transparent backdrop-blur-sm">
        <button
          type="button"
          onClick={() =>
            navigate({ to: "/business/$slug", params: { slug }, replace: true })
          }
          className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-bold text-foreground flex-1">
          {t("business.cart.title")}
        </h1>
        <AuthButton size="sm" />
      </div>

      <div className="flex-1 overflow-y-auto pb-32">
        <AnimatePresence mode="wait" initial={false}>
          {items.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              className="flex flex-col items-center justify-center py-20 gap-6 px-8 text-center"
            >
              <div className="relative w-36 h-36">
                <div className="w-36 h-36 rounded-full bg-muted flex items-center justify-center">
                  <img
                    src="/smile.svg"
                    alt=""
                    aria-hidden="true"
                    className="w-20 h-20 dark:invert"
                  />
                </div>
                <img
                  src="/heart.svg"
                  alt=""
                  aria-hidden="true"
                  className="absolute -top-2 -right-1 w-9 h-9 dark:invert"
                />
                <img
                  src="/heart.svg"
                  alt=""
                  aria-hidden="true"
                  className="absolute bottom-1 -left-4 w-6 h-6 dark:invert opacity-50 -rotate-12"
                />
              </div>
              <p className="text-xl font-bold text-foreground">
                {t("business.cart.empty")}
              </p>
              <Button
                className="rounded-full px-8"
                onClick={() =>
                  navigate({
                    to: "/business/$slug",
                    params: { slug },
                    replace: true,
                  })
                }
              >
                {t("business.cart.continueShopping")}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="items"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="max-w-2xl mx-auto w-full px-4 pt-2"
            >
              <AnimatePresence initial={false}>
                {items.map((item) => (
                  <motion.div
                    key={item.cartItemId}
                    layout="position"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    transition={{
                      layout: { type: "spring", stiffness: 400, damping: 35 },
                      opacity: { duration: 0.18 },
                      height: { type: "spring", stiffness: 400, damping: 35 },
                      y: { type: "spring", stiffness: 400, damping: 30 },
                    }}
                    className="overflow-hidden"
                  >
                    <CartItemRow item={item} />
                  </motion.div>
                ))}
              </AnimatePresence>

              <button
                type="button"
                onClick={() => setNoteOpen(true)}
                className="w-full flex items-center justify-between bg-muted rounded-2xl px-4 py-3.5 mt-4 text-left hover:bg-muted/70 transition-colors"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <MessageCircle
                    size={16}
                    className="text-muted-foreground shrink-0"
                    animate="default"
                  />
                  {cartComment ? (
                    <span className="text-sm text-foreground truncate">
                      {cartComment}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {t("common.actions.orderNote")}
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
              <OrderNoteSheet
                open={noteOpen}
                onClose={() => setNoteOpen(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-background via-background/80 to-transparent pt-6 pb-4">
          <div className="px-4 w-full max-w-2xl mx-auto">
            {noPaymentMethods && (
              <p
                data-testid="cart.no-payment-methods"
                className="mb-3 text-center text-sm text-muted-foreground"
              >
                {t("business.cart.noPaymentMethods")}
              </p>
            )}
            <Button
              data-testid="cart.checkout.button"
              disabled={noPaymentMethods}
              className="w-full flex items-center justify-center gap-1.5 rounded-2xl h-12 text-base shadow-lg backdrop-blur-[2px]"
              onClick={() => {
                if (isDineIn) {
                  if (isAuth) {
                    goToCheckout();
                  } else if (guestInfoLevel) {
                    setCompleteProfileCallback(() => goToCheckout);
                    openCompleteProfile(true);
                  } else {
                    silentGuestLogin();
                  }
                  return;
                }
                if (!isAuth) {
                  openLoginSheet(true);
                } else if (!isProfileComplete(currentUser, guestDetails)) {
                  setCompleteProfileCallback(() => goToCheckout);
                  openCompleteProfile(true);
                } else {
                  goToCheckout();
                }
              }}
            >
              <span className="font-semibold">
                {formatPrice(totalPrice, currency)}
              </span>
              <span className="font-normal opacity-80">·</span>
              <span className="font-normal">
                {isAuth
                  ? t("business.cart.checkout")
                  : t("common.actions.next")}
              </span>
            </Button>
          </div>
        </div>
      )}

      <LoginSheet />
    </motion.div>
  );
}
